import React from 'react';
import { useI18n, useFormatNumber, useFormatDate } from '@udemy/i18n';
import { RatingTag, RibbonTag, Tag } from '@udemy/react-product-card-components';
import { getNumLecturesText } from './utilities';

/**
 * ### CourseCardCourseTag
 *
 * @remarks
 *
 * Renders a tag that says 'Course', to label a product card as representing a course.
 */
export const CourseCardCourseTag = () => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Tag, null, gettext('Course'));
};

/**
 * ### CourseCardRatingTag
 *
 * @remarks
 *
 * Renders a `RatingTag` from a `Course` object.
 *
 * @see {@link @udemy/react-product-card-components#RatingTag}
 */
export const CourseCardRatingTag = ({
  course
}) => /*#__PURE__*/React.createElement(RatingTag, {
  rating: course.rating
});
/**
 * ### CourseCardRatingTag
 *
 * @remarks
 *
 * Renders a tag with the rating count from a `Course` object
 */
export const CourseCardRatingCountTag = ({
  course: {
    rating,
    num_reviews: numReviews
  },
  reviewsCountText
}) => {
  const {
    ninterpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  if (!rating) {
    return null;
  }
  const label = reviewsCountText || ninterpolate('%(count)s rating', '%(count)s ratings', numReviews, {
    count: formatNumber(numReviews)
  });
  return /*#__PURE__*/React.createElement(Tag, null, label);
};

/**
 * ### CourseCardContentInfoTag
 *
 * @remarks
 *
 * Renders a tag with the content_info property from a `Course` object
 */
export const CourseCardContentInfoTag = ({
  course
}) => course.content_info ? /*#__PURE__*/React.createElement(Tag, null, course.content_info) : null;

/**
 * ### CourseCardPublishedLecturesTag
 *
 * @remarks
 *
 * Renders a tag with the number of a published lectures in a course
 * from a `Course` object
 */
export const CourseCardPublishedLecturesTag = ({
  course
}) => {
  const {
    ninterpolate
  } = useI18n();
  return course.num_published_lectures > 0 ? /*#__PURE__*/React.createElement(Tag, null, getNumLecturesText(course.num_published_lectures, {
    ninterpolate
  })) : null;
};

/**
 * ### CourseCardInstructorLevelTag
 *
 * @remarks
 *
 * Renders a tag with the instructors level from a `Course` object
 */
export const CourseCardInstructorLevelTag = ({
  course
}) => course.instructional_level_simple ? /*#__PURE__*/React.createElement(Tag, null, course.instructional_level_simple) : null;

/**
 * ### CourseCardLastUpdatedTag
 *
 * @remarks
 *
 * Renders a tag with the last updated date.
 */
export const CourseCardLastUpdatedTag = ({
  course
}) => {
  const {
    interpolate,
    gettext
  } = useI18n();
  const {
    toLocaleDateString
  } = useFormatDate();
  const date = new Date(course.last_update_date);
  if (!course.last_update_date || !(date instanceof Date)) {
    return null;
  }
  const lastUpdateDate = toLocaleDateString(date, {
    day: undefined,
    month: 'numeric',
    year: 'numeric'
  });
  return /*#__PURE__*/React.createElement(Tag, null, interpolate(gettext('Updated %s'), [lastUpdateDate]));
};

/**
 * ### ribbonsToRibbonTags
 *
 * @remarks
 *
 * Wraps `Ribbon` components in `RibbonTag` components.
 *
 * @see {@link @udemy/react-product-card-components#RibbonTag}
 */
export const ribbonsToRibbonTags = ribbonElements => ribbonElements.map((ribbon, index) => /*#__PURE__*/React.createElement(RibbonTag, {
  key: index
}, ribbon));
//# sourceMappingURL=tags.react-component.js.map